import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {LocationCardScratchpadState} from '../../scratchpad/locationCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import locationFeedApi from 'common/src/modules/locationFeed/locationFeed.api';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';

const setLocationFeedToolSchema = z.object({
  feedId: z.string().describe(describeIdFormat(IdPrefix.LocationFeed)),
});

export default function setLocationFeedTool() {
  async function execute(
    params: ToolExecuteParams<typeof setLocationFeedToolSchema, LocationCardScratchpadState>,
  ) {
    const {feedId} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<LocationCardScratchpadState>(
      agentScratchpad,
      NodeType.STORE,
    );

    if (!feedId) {
      return toolError('No feed ID provided');
    }

    const parseResult = idHelper.parseLocationFeedId(feedId);

    if (parseResult.ok === false) {
      return toolError(formatParseError(parseResult.error));
    }

    const numericId = parseResult.numericId;
    const availableFeeds = await locationFeedApi.getFeedsNew();

    if (!availableFeeds.some((f) => f.id === numericId)) {
      return toolError(
        `Location feed ${idHelper.toLocationFeedId(numericId)} is not available. Available location feeds: ${availableFeeds.map((f) => `${idHelper.toLocationFeedId(f.id)}: ${f.name}`).join(', ')}`,
      );
    }

    validatedScratchpad.card.locationFeedId = numericId;

    return toolResult(`Location feed set to: ${idHelper.toLocationFeedId(numericId)}`);
  }

  return tool({
    name: V2CardAgentToolName.SetLocationFeed,
    displayName: 'Setting location feed',
    description: 'Set the location feed for the card.',
    execute,
    inputSchema: setLocationFeedToolSchema,
  });
}
